import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  Bar,
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  DataPopper,
  DataPopperRow,
  Stack,
  TimeXAxis,
  tokens,
  Typography,
  YAxis,
} from '@cast/design-system';
import { formatCurrency, getCurrencyPrecision } from '@cast/utils';

import type { WorkloadResourceCostChartPoint } from '../_hooks/useWorkloadResourceCostChartData';

dayjs.extend(utc);

type Props = {
  data: WorkloadResourceCostChartPoint[];
  isLoading: boolean;
  hasNoData?: boolean;
  timeZone: string;
  tooltipTestId?: string;
  testId?: string;
  /** Whether to show On-demand / Fallback / Spot rows in the tooltip. */
  hasOfferingBreakdown: boolean;
};

const ChartTooltipRow = ({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) => {
  const renderMoney = (n: number) => (
    <Typography variant="P10R" align="right">
      {formatCurrency(n, {
        maximumFractionDigits: getCurrencyPrecision(n),
      })}
    </Typography>
  );

  return (
    <DataPopperRow
      left={
        <Typography variant="L10R" color={tokens.theme.fg.structural.primary}>
          {label}
        </Typography>
      }
      right={
        value !== null ? (
          renderMoney(value)
        ) : (
          <Typography variant="P10R" align="right">
            —
          </Typography>
        )
      }
    />
  );
};

const ChartTooltipFooter = ({
  data,
}: {
  data: WorkloadResourceCostChartPoint;
}) => {
  const cost = data.normalized;

  const renderMoney = (n: number) => (
    <Typography variant="P10R" align="right">
      {formatCurrency(n, {
        maximumFractionDigits: getCurrencyPrecision(n),
      })}
    </Typography>
  );

  return (
    <DataPopperRow
      left={
        <Typography variant="P10R" color={tokens.theme.fg.structural.secondary}>
          Normalized
        </Typography>
      }
      right={
        cost !== null ? (
          renderMoney(cost)
        ) : (
          <Typography variant="P10R" align="right">
            —
          </Typography>
        )
      }
    />
  );
};

export const ResourceCostChart = ({
  data,
  isLoading,
  hasNoData,
  timeZone,
  tooltipTestId,
  testId,
  hasOfferingBreakdown,
}: Props) => {
  const renderTooltip = (tooltipData: WorkloadResourceCostChartPoint) => (
    <DataPopper size="small" sx={{ minWidth: 180 }}>
      <Stack gap={4}>
        <ChartTooltipTimestampHeader
          data={tooltipData}
          hideTime
          testId={tooltipTestId ?? testId ?? 'resource-cost-chart-tooltip'}
          timezone={timeZone}
        />
        {hasOfferingBreakdown && (
          <>
            <ChartTooltipRow label="On-demand" value={tooltipData.onDemand} />
            <ChartTooltipRow label="Fallback" value={tooltipData.fallback} />
            <ChartTooltipRow label="Spot" value={tooltipData.spot} />
          </>
        )}
        <ChartTooltipFooter data={tooltipData} />
      </Stack>
    </DataPopper>
  );

  return (
    <Box flex={1} minHeight={0}>
      <ChartContainer<WorkloadResourceCostChartPoint>
        data={data}
        isLoading={isLoading}
        hasNoData={hasNoData}
        sx={{ height: '100%' }}
        barConfig={{ barSize: 20, maxBarSize: 20 }}
        testId={testId}
      >
        <ChartGrid hideVertical />
        <YAxis<WorkloadResourceCostChartPoint>
          tickFormatter={(value) => `$${value}`}
          allowDecimals
          tickCount={3}
          padding={{ top: 12 }}
        />
        <TimeXAxis<WorkloadResourceCostChartPoint>
          unixDataKey="chartTimestampUnix"
          timezone={timeZone}
        />
        <Bar<WorkloadResourceCostChartPoint>
          dataKey="normalized"
          fill={tokens.theme.data.blue700}
          getBarPropsByPoint={(point) => ({
            fillOpacity: point.forecast ? 0.3 : 1,
          })}
          tooltipConfig={{
            hidden: true,
          }}
        />
        <ChartTooltip<WorkloadResourceCostChartPoint>
          testId={tooltipTestId}
          TooltipComponent={({ payload }) => {
            const point = payload?.[0]?.payload;

            if (!point) {
              return null;
            }

            return renderTooltip(point);
          }}
        />
      </ChartContainer>
    </Box>
  );
};
